import React, { useState, useEffect } from 'react';
import { StackItem } from './core/StackItem';
import { StackItemGrid } from './core/StackItemGrid';
import { Capture } from './Capture';
import { useStack } from './core/StackContext';
import { StackProvider } from './core/StackContext';
import { getTemplateCache } from './services/templateCache';
import DetailResolver from './core/DetailResolver';

// Import CardCraft and QwantaProvider for detail view
import { DNDEditor } from '../editor/DNDEditor';
import { QwantaProvider } from 'qwanyx';
import { IconButton, Select, MenuItem, FormControl } from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";

// Check if we're inside a StackProvider
const useStackSafely = () => {
    try {
        return useStack();
    } catch (error) {
        return null;
    }
};

// DetailTemplateRenderer Component
const DetailTemplateRenderer = ({ item, templateVi, useCompact, debug = false }) => {
    const [processedTemplate, setProcessedTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        if (!templateVi || !item) {
            setLoading(false);
            return;
        }
        
        if (debug) {
            console.log(`[DetailTemplateRenderer] Loading template ${templateVi} for item ${item._id}`);
        }
        
        setLoading(true);
        // Force refresh on mount to ensure fresh templates during development
        getTemplateCache().fetchTemplate(templateVi, true)
            .then(template => {
                const processed = getTemplateCache().replaceTemplateVariables(template, item);
                setProcessedTemplate(processed);
                setLoading(false);
                
                if (debug) {
                    console.log(`[DetailTemplateRenderer] Template processed for item ${item._id}`);
                }
            })
            .catch(error => {
                console.error("[DetailTemplateRenderer] Error loading detail template:", error);
                setLoading(false);
            });
    }, [templateVi, item, debug]);
    
    if (loading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>Loading template...</div>
        );
    }
    
    if (!processedTemplate) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                Template not found
            </div>
        );
    }
    
    return (
        <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
            <QwantaProvider _id={item._id}>
                <DNDEditor
                    cardData={processedTemplate}
                    enabled={false}
                    readOnly={true}
                    stackMode={false}
                    compact={useCompact}
                />
            </QwantaProvider>
        </div>
    );
};

const StackContainerInner = ({
    showDetailView = false,
    detailIds = [],
    showViewSelector = true,
    DetailComponent = null,
    showCapture = true,
    showSelectionInfo = true,
    sideBySide = false,
    listWidth = "350px",
    onSelect,
    // Grid props
    viewMode = "tree",
    columnsPerRow = 3,
    imageWidth = "64px",
    ratio = "1/1",
    imageClassName = "",
    textClassName = "",
    animateClass = "",
    // Template props for list items
    itemDisplayVi = null,
    useCompactRenderer = true,
    useFullTemplateMode = true,
    // Template props for detail views
    detailDisplayVi = null,
    useDetailCompactRenderer = false,
    // NEW: DetailResolver props
    defaultDetailTemplate = null,
    forceDefaultDetail = false
}) => {
    // Template state at container level
    const [templateData, setTemplateData] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [processedTemplates, setProcessedTemplates] = useState(new Map());

    // Get everything from StackContext - ENHANCED with local-first methods
    const {
        selectedItems,
        lastSelectedItem,
        flattenedItems,
        cardDataMap,
        selectedItemForDetail,
        refreshTrigger,                   // NEW: Get refresh trigger
        expandedItems,                    // NEW: Get expanded state
        folderFilters,                    // NEW: Get folder filters
        recentlyCreatedItems,             // NEW: Get recently created items
        effectiveRootId,
        isCurrentCardVisible,
        setSelectedItemForDetail,
        handleSelect: contextHandleSelect,
        handleDuplicateItem,
        handleNewItem,
        handleMoveItem,        // NEW: Local-first move handler
        handleUpdateTitle,     // NEW: Local-first title handler  
        handleMakeFolder,      // NEW: Local-first folder handler
        toggleExpanded,        // NEW: Expanded state handler
        isExpanded,            // NEW: Expanded state checker
        toggleFolderFilter,    // NEW: Per-folder filter toggle
        updateFolderFilterText, // NEW: Per-folder filter text update
        getFolderFilter,       // NEW: Get folder filter state
        markAsRecentlyCreated, // NEW: Mark item as recently created
        isRecentlyCreated,     // NEW: Check if item is recently created
        registerItem,
        handleToggleCurrentCard,
        handleBackToList,
        handleDetailIdChange,
        debugLog,
        debug,
        rows,
        // Legacy method (kept for compatibility)
        refreshAfterDrop
    } = useStack();

    // Load template once when itemDisplayVi changes
    useEffect(() => {
        if (debug) {
            console.log("[StackContainer] Cache debug:", {
                templateCache: getTemplateCache(),
                cacheSize: getTemplateCache().getCacheSize(),
                itemDisplayVi
            });
        }

        if (itemDisplayVi) {
            setTemplateLoading(true);
            // Force refresh on mount to ensure fresh templates during development
            getTemplateCache().fetchTemplate(itemDisplayVi, true)
                .then(template => {
                    setTemplateData(template);
                    setTemplateLoading(false);
                    if (debug) {
                        console.log("[StackContainer] Template loaded with refresh:", itemDisplayVi);
                    }
                })
                .catch(error => {
                    console.error("[StackContainer] Error loading template:", error);
                    setTemplateLoading(false);
                });
        } else {
            setTemplateData(null);
            setProcessedTemplates(new Map());
        }
    }, [itemDisplayVi, debug]);

    // Process templates for all items when cardDataMap or templateData changes
    useEffect(() => {
        if (templateData && Object.keys(cardDataMap).length > 0) {
            const newProcessedTemplates = new Map();

            Object.entries(cardDataMap).forEach(([itemId, itemData]) => {
                console.log(`Processing item ${itemId}:`, itemData);
                try {
                    const processedTemplate = getTemplateCache().replaceTemplateVariables(templateData, itemData);
                    newProcessedTemplates.set(itemId, processedTemplate);
                    console.log(`✅ Processed template for item ${itemId}`);
                } catch (error) {
                    console.error(`❌ Error processing template for item ${itemId}:`, error);
                }
            });

            setProcessedTemplates(newProcessedTemplates);

            if (debug) {
                console.log(`Processed templates for ${newProcessedTemplates.size} items`);
            }
        }
    }, [templateData, cardDataMap, debug]);

    // Wrapper for handleSelect to pass the additional parameters
    const handleSelect = (itemId, isShiftKey, isCtrlKey) => {
        contextHandleSelect(itemId, isShiftKey, isCtrlKey, showDetailView, onSelect);
    };

    // Simple vi management - Stack decides everything
    const getStackSelectedVi = () => {
        if (!selectedItemForDetail?._id || detailIds.length === 0) {
            return null;
        }

        // Stack is the source of truth for vi
        const stackVi = selectedItemForDetail.vi;
        const isValidVi = detailIds.some(detail => detail.id === stackVi);

        if (debug) {
            console.log(`[Stack Owner] getStackSelectedVi:`, {
                itemId: selectedItemForDetail._id,
                stackVi,
                isValidVi,
                availableViews: detailIds.map(d => ({ name: d.name, id: d.id }))
            });
        }

        // Return valid vi or first available
        return isValidVi ? stackVi : (detailIds[0]?.id || null);
    };

    const renderDetailView = () => {
        if (debug) {
            console.log(`[Stack Owner] renderDetailView with DetailResolver:`, {
                selectedItemId: selectedItemForDetail?._id,
                DetailComponent: !!DetailComponent,
                defaultDetailTemplate,
                forceDefaultDetail,
                detailDisplayVi,
                useDetailCompactRenderer,
                selectedItemForDetail
            });
        }

        // Use legacy mode if detailIds are configured (backwards compatibility)
        const useLegacyMode = detailIds.length > 0;
        
        if (useLegacyMode) {
            const currentVi = getStackSelectedVi();
            
            // Legacy rendering for backwards compatibility
            return (
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {renderDetailHeader(currentVi)}
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {DetailComponent ? (
                            <DetailComponent item={selectedItemForDetail} />
                        ) : detailDisplayVi ? (
                            <DetailTemplateRenderer
                                item={selectedItemForDetail}
                                templateVi={detailDisplayVi}
                                useCompact={useDetailCompactRenderer}
                                debug={debug}
                            />
                        ) : currentVi ? (
                            <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
                                <QwantaProvider _id={selectedItemForDetail._id}>
                                    <DNDEditor
                                        vi={currentVi}
                                        enabled={false}
                                        stackMode={false}
                                        readOnly={true}
                                    />
                                </QwantaProvider>
                            </div>
                        ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                <div style={{ fontSize: '16px', marginBottom: '8px' }}>⚠️ No Template Available</div>
                                <div style={{ fontSize: '14px' }}>Configure a detail template or valid detail IDs</div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // NEW: Use DetailResolver for modern flexible detail rendering
        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {renderDetailHeader(null)}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <DetailResolver
                        item={selectedItemForDetail}
                        DetailComponent={DetailComponent}
                        defaultDetailTemplate={defaultDetailTemplate || detailDisplayVi}
                        forceDefaultDetail={forceDefaultDetail}
                        useCompact={useDetailCompactRenderer}
                        debug={debug}
                    />
                </div>
            </div>
        );
    };

    const renderDetailHeader = (currentVi) => {
        // Stack controls when to show dropdown - hide when using detail template
        const shouldShowDropdown = showViewSelector && detailIds.length > 1 && !detailDisplayVi;

        const dropdownContent = shouldShowDropdown && (
            <FormControl size="small" style={{ minWidth: 150 }}>
                <Select
                    value={currentVi || ""}
                    onChange={handleDetailIdChange}
                    displayEmpty
                    style={{ fontSize: '14px' }}
                >
                    {!currentVi && (
                        <MenuItem value="" disabled>
                            <em>Select a view</em>
                        </MenuItem>
                    )}
                    {detailIds.map((view) => (
                        <MenuItem key={view.id} value={view.id}>
                            {view.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );

        if (sideBySide) {
            return dropdownContent && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px',
                    borderBottom: '1px solid #eee',
                    backgroundColor: '#f8f9fa',
                    gap: '8px'
                }}>
                    <span style={{ fontSize: '12px', color: '#666' }}>View:</span>
                    {dropdownContent}
                </div>
            );
        }

        return dropdownContent ? (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa',
                gap: '8px'
            }}>
                <IconButton onClick={handleBackToList} size="small">
                    <ArrowBackIcon />
                </IconButton>
                <span style={{ fontSize: '12px', color: '#666' }}>View:</span>
                {dropdownContent}
            </div>
        ) : (
            // When no dropdown, just show back button (for detail template case)
            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa'
            }}>
                <IconButton onClick={handleBackToList} size="small">
                    <ArrowBackIcon />
                </IconButton>
            </div>
        );
    };

    // Function to get processed template for a specific item
    const getProcessedTemplateForItem = (itemId) => {
        console.log("🔍 DEBUG:", {
            itemId,
            itemDisplayVi,
            templateData: !!templateData,
            cardDataMapSize: Object.keys(cardDataMap).length,
            processedTemplatesSize: processedTemplates.size
        });

        if (!itemDisplayVi || !processedTemplates.has(itemId)) {
            return null;
        }
        return processedTemplates.get(itemId);
    };

    // Stack renders its own views
    const renderStackView = () => {
        if (templateLoading && itemDisplayVi) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    Loading template...
                </div>
            );
        }

        if (viewMode === "grid") {
            return (
                <StackItemGrid
                    itemId={effectiveRootId}
                    isRoot={true}
                    onSelect={handleSelect}
                    selectedItems={selectedItems}
                    registerItem={registerItem}
                    onDrop={refreshAfterDrop}
                    handleMoveItem={handleMoveItem}  // NEW: Pass local-first handler
                    rows={rows}
                    disableCurrentCardUpdate={showDetailView}
                    columnsPerRow={columnsPerRow}
                    imageWidth={imageWidth}
                    ratio={ratio}
                    imageClassName={imageClassName}
                    textClassName={textClassName}
                    animateClass={animateClass}
                />
            );
        }

        // Enhanced StackItem that processes templates at container level
        return (
            <EnhancedStackItem
                itemId={effectiveRootId}
                isRoot={true}
                onSelect={handleSelect}
                selectedItems={selectedItems}
                registerItem={registerItem}
                handleMoveItem={handleMoveItem}           // NEW: Pass local-first handlers
                handleUpdateTitle={handleUpdateTitle}     // NEW: Pass local-first handlers
                handleMakeFolder={handleMakeFolder}       // NEW: Pass local-first handlers
                refreshTrigger={refreshTrigger}           // NEW: Pass refresh trigger
                toggleExpanded={toggleExpanded}           // NEW: Pass expanded handlers
                isExpanded={isExpanded}                   // NEW: Pass expanded checker
                toggleFolderFilter={toggleFolderFilter}   // NEW: Pass filter methods
                updateFolderFilterText={updateFolderFilterText}
                getFolderFilter={getFolderFilter}
                isRecentlyCreated={isRecentlyCreated}     // NEW: Pass recently created checker
                rows={rows}
                disableCurrentCardUpdate={showDetailView}
                getProcessedTemplateForItem={getProcessedTemplateForItem}
                useCompactRenderer={useCompactRenderer}
                useFullTemplateMode={useFullTemplateMode}
            />
        );
    };

    return (
        <>
            {/* Stack controls its capture */}
            <div className="p-1">
                {(!selectedItemForDetail || sideBySide) && showCapture && (
                    <Capture
                        onDuplicate={handleDuplicateItem}
                        onNewItem={handleNewItem}
                        selectedItems={selectedItems}
                        stackRootId={effectiveRootId}
                        cardDataMap={cardDataMap}
                    />
                )}
            </div>

            {/* Stack layout */}
            <div style={{
                height: showCapture ? 'calc(100% - 85px)' : '100%',
                display: 'flex',
                flexDirection: 'row'
            }}>
                {/* Stack list view */}
                <div style={{
                    width: sideBySide ? listWidth : '100%',
                    overflow: 'auto',
                    borderRight: sideBySide ? '1px solid #eee' : 'none',
                    flexShrink: 0,
                    display: sideBySide ? 'block' : (selectedItemForDetail ? 'none' : 'block')
                }}>
                    {renderStackView()}
                </div>

                {/* Stack detail view */}
                {selectedItemForDetail && (
                    <div style={{
                        flex: sideBySide ? 1 : 'none',
                        width: sideBySide ? 'auto' : '100%',
                        overflow: 'hidden',
                        borderLeft: sideBySide ? '1px solid #eee' : 'none'
                    }}>
                        {renderDetailView()}
                    </div>
                )}
            </div>
        </>
    );
};

// Enhanced StackItem wrapper that handles template processing and local-first operations
const EnhancedStackItem = ({
    itemId,
    isRoot,
    onSelect,
    selectedItems,
    registerItem,
    handleMoveItem,
    handleUpdateTitle,
    handleMakeFolder,
    refreshTrigger,
    toggleExpanded,
    isExpanded,
    toggleFolderFilter,    // NEW: Per-folder filter methods
    updateFolderFilterText,
    getFolderFilter,
    isRecentlyCreated,     // NEW: Recently created checker
    rows,
    disableCurrentCardUpdate,
    getProcessedTemplateForItem,
    useCompactRenderer,
    ...props
}) => {
    return (
        <StackItem
            itemId={itemId}
            isRoot={isRoot}
            onSelect={onSelect}
            selectedItems={selectedItems}
            registerItem={(id, level, itemData) => {
                // Register item normally
                if (registerItem) {
                    registerItem(id, level, itemData);
                }
            }}
            handleMoveItem={handleMoveItem}
            handleUpdateTitle={handleUpdateTitle}
            handleMakeFolder={handleMakeFolder}
            refreshTrigger={refreshTrigger}
            toggleExpanded={toggleExpanded}
            isExpanded={isExpanded}
            toggleFolderFilter={toggleFolderFilter}         // NEW: Pass filter methods
            updateFolderFilterText={updateFolderFilterText}
            getFolderFilter={getFolderFilter}
            isRecentlyCreated={isRecentlyCreated}           // NEW: Pass recently created checker
            rows={rows}
            disableCurrentCardUpdate={disableCurrentCardUpdate}
            getProcessedTemplateForItem={getProcessedTemplateForItem}
            useCompactRenderer={useCompactRenderer}
            {...props}
        />
    );
};

export const StackContainer = ({
    rootId,
    debug = false,
    rows = 100,
    useCompactRenderer = true,
    // Accept detail template props
    detailDisplayVi = null,
    useDetailCompactRenderer = false,
    useFullTemplateMode = true,
    // NEW: DetailResolver props
    defaultDetailTemplate = null,
    forceDefaultDetail = false,
    ...props
}) => {
    const stackContext = useStackSafely();

    if (stackContext) {
        return (
            <QwantaProvider _id={stackContext.lastSelectedItem || "guest"}>
                <StackContainerInner 
                    useCompactRenderer={useCompactRenderer}
                    detailDisplayVi={detailDisplayVi}
                    useDetailCompactRenderer={useDetailCompactRenderer}
                    defaultDetailTemplate={defaultDetailTemplate}
                    forceDefaultDetail={forceDefaultDetail}
                    {...props} 
                />
            </QwantaProvider>
        );
    }

    // If not, wrap with StackProvider for backward compatibility
    return (
        <StackProvider rootId={rootId} debug={debug} rows={rows}>
            <StackContainerInner 
                useCompactRenderer={useCompactRenderer}
                detailDisplayVi={detailDisplayVi}
                useDetailCompactRenderer={useDetailCompactRenderer}
                defaultDetailTemplate={defaultDetailTemplate}
                forceDefaultDetail={forceDefaultDetail}
                {...props} 
            />
        </StackProvider>
    );
};

export default StackContainer;